import type { LeviathanCaptain } from "../character.js";
import {
  ATTRIBUTE_IDS,
  BALANCED_ATTRIBUTE_IDS,
  MAX_BALANCE_SPREAD,
  costForRating,
  isPurchasableRating,
  maxRankFor,
} from "../data/attributes.js";
import { reputationFor, reputationTierFor } from "@personnel/setting";
import type { ReputationPathId } from "@personnel/setting";
import { essenceCostFor } from "../data/essence.js";
import { recommendedRenownFor } from "../data/reputation.js";
import { EXPERIENCE_LEVELS } from "../data/experience.js";
import { QUALITY_COST, qualityById } from "../data/qualities.js";

/**
 * Printed cards round Cost for play. The engine reports the exact figure, but
 * a captain sitting beside published cards needs to match them -- the sample
 * card 62002-01 shows 10 for a captain this engine costs at 12.
 */
export const ROUND_COST_TO = 5;

export function roundCost(cost: number): number {
  return Math.round(cost / ROUND_COST_TO) * ROUND_COST_TO;
}

export interface LeviathanDerived {
  /** Total Cost of all attribute ratings. */
  attributeCost: number;
  /** Total Cost of all Qualities. */
  qualityCost: number;
  /** Experience spent on Essence. Excluded from Cost by rule. */
  essenceCost: number;
  /** Essence held but not yet spent in play. */
  essenceRemaining: number;
  /** The captain's final Cost. Attributes and Qualities only -- not Essence. */
  totalCost: number;
  /** Cost to the nearest 5, as printed cards show it. */
  roundedCost: number;
  /**
   * Everything the captain has actually spent Experience on, Essence included.
   * Diverges from `totalCost` precisely because Essence does not raise Cost.
   */
  experienceCommitted: number;

  /** Reputation Tier, 1-5, from Renown. */
  reputationTier: number;
  /** Reputation title. Null above Tier 1 until a path is chosen. */
  reputation: string | null;
  /** Suggested starting Renown for this Cost. Advisory only. */
  recommendedRenown: number;
  /** Spending cap, or null when building by Cost rather than Experience Level. */
  experienceAvailable: number | null;
  /** Cap minus spend. Negative means overspent. Null when building by Cost. */
  experienceRemaining: number | null;
  /** Rank after any Quality that grants one, e.g. Distinguished Career. */
  effectiveRank: number;
  /** Highest Rank this captain may purchase, from the other five attributes. */
  maxRank: number;
  /** True when unspent Experience is forfeited rather than carried. */
  unspentIsLost: boolean;
  /** Highest balanced attribute currently held. */
  highestBalanced: number;
  /** Lowest balanced attribute currently held. */
  lowestBalanced: number;
  /** The floor every balanced attribute must reach, given the current highest. */
  balanceFloor: number;
}

function balancedRatings(captain: LeviathanCaptain): number[] {
  return BALANCED_ATTRIBUTE_IDS.map((id) => captain.attributes[id]);
}

export function attributeCostOf(captain: LeviathanCaptain): number {
  return ATTRIBUTE_IDS.reduce((total, id) => {
    const rating = captain.attributes[id];
    // Out-of-range ratings are reported by validation; cost them as zero here
    // so a bad value cannot throw while the player is mid-edit.
    return total + (isPurchasableRating(rating) ? costForRating(rating) : 0);
  }, 0);
}

export function qualityCostOf(captain: LeviathanCaptain): number {
  return captain.qualities.length * QUALITY_COST;
}

export function effectiveRankOf(captain: LeviathanCaptain): number {
  return captain.qualities.reduce((rank, id) => {
    const granted = qualityById(id)?.grantsRank;
    return granted !== undefined && granted > rank ? granted : rank;
  }, captain.attributes.rank);
}

export function derive(captain: LeviathanCaptain): LeviathanDerived {
  const attributeCost = attributeCostOf(captain);
  const qualityCost = qualityCostOf(captain);
  const totalCost = attributeCost + qualityCost;
  const essenceCost = essenceCostFor(Math.max(0, captain.essence));
  const reputationTier = reputationTierFor(Math.max(0, captain.renown));

  const level =
    captain.buildMode === "level" && captain.experienceLevel !== null
      ? EXPERIENCE_LEVELS[captain.experienceLevel]
      : null;

  // A level's allowance, or the pool the player carries. Cost mode has neither.
  const experienceAvailable =
    level !== null
      ? level.experienceAvailable
      : captain.buildMode === "points" && captain.experiencePoints !== null
        ? Math.max(0, captain.experiencePoints)
        : null;

  // Essence draws on the same pool as attributes and Qualities, so the budget
  // is measured against everything committed rather than against Cost alone.
  const spent = totalCost + essenceCost;

  const ratings = balancedRatings(captain);
  const highestBalanced = Math.max(...ratings);
  const lowestBalanced = Math.min(...ratings);

  return {
    attributeCost,
    qualityCost,
    essenceCost,
    essenceRemaining: captain.essence - captain.essenceUsed,
    totalCost,
    roundedCost: roundCost(totalCost),
    experienceCommitted: totalCost + essenceCost,
    reputationTier,
    reputation: reputationFor(reputationTier, captain.reputationPath as ReputationPathId | null),
    recommendedRenown: recommendedRenownFor(totalCost),
    experienceAvailable,
    experienceRemaining: experienceAvailable === null ? null : experienceAvailable - spent,
    effectiveRank: effectiveRankOf(captain),
    maxRank: maxRankFor(captain.attributes),
    unspentIsLost: captain.buildMode === "level",
    highestBalanced,
    lowestBalanced,
    balanceFloor: Math.max(0, highestBalanced - MAX_BALANCE_SPREAD),
  };
}
